import { useMemo } from 'react'
import { useOpsStatus } from './opsStatus'

/**
 * Detects when the running backend is newer than the bundle this browser
 * tab has loaded.
 *
 * ClickPay is a single-page app with per-record keep-alive tabs (see
 * tabs/TabsHost's docblock) and NO live-tab update mechanism — main.tsx
 * only force-reloads on a chunk-load ERROR, never on a plain version bump.
 * An operator who leaves a tab open across a deploy keeps running the OLD
 * bundle indefinitely, with no signal that a fix has shipped. That's
 * exactly how the restock-flicker fix (61631da) reached prod this morning
 * but didn't reach an already-open tab until the owner manually hard-
 * refreshed (feedback #15).
 *
 * Deliberately reuses the ALREADY-POLLED shared ops-status snapshot
 * (lib/opsStatus.ts's Build 52a single-flight rollup) instead of opening a
 * second independent poll loop: that store already fetches /api/health on
 * the same 30s cadence, already pauses while the browser tab is hidden, and
 * already does one immediate refresh on tab-resume (visibilitychange) — the
 * exact cadence/visibility behavior a standalone hook would have to
 * reimplement. Piggybacking costs zero additional requests; a second timer
 * here is the mistake clickpay-polling-request-storm (memory) was about.
 *
 * __BUILD_VERSION__ is injected by vite.config.ts from `git rev-parse
 * --short HEAD` at build time — the same short-hash format backend/main.py
 * `_resolve_version()` reports as /api/health's `version` field.
 */
export function useVersionSkew(): { stale: boolean; remoteVersion: string | null } {
  const { s } = useOpsStatus()
  const remoteVersion = s?.health.data?.version ?? null
  const stale = useMemo(
    () => remoteVersion != null && remoteVersion !== __BUILD_VERSION__,
    [remoteVersion],
  )
  return { stale, remoteVersion }
}
